/**
 * What a page that threw draws in its place (contexts/25): a chunk that still would not load after
 * the one reload `loadOrReload` makes, or an error in a page's own render. Without a boundary React
 * unmounts the whole app — the white screen. Inside the shell the menu stays and a move to another
 * address clears it (`resetKey`); the outer one in App.tsx catches the shell itself.
 */

import { Component, Suspense, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
  /** A change clears the error: the shell passes the path. */
  readonly resetKey?: string;
}

interface State {
  readonly failed: boolean;
}

export class PageErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="page-pad">
        <div className="callout callout--bad" role="alert">
          <strong>This page could not load.</strong>{' '}
          <button type="button" className="button button--small" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

/** Where a page lazily loads: Loading… while its chunk arrives, the boundary's message if it throws. */
export function PageBoundary({ children, resetKey }: Props) {
  return (
    <PageErrorBoundary {...(resetKey === undefined ? {} : { resetKey })}>
      <Suspense fallback={<p className="text-muted page-pad">Loading…</p>}>{children}</Suspense>
    </PageErrorBoundary>
  );
}
